import { useEffect, useState } from "react";
import { store } from "../lib/store.js";

//styles
import Logo from "../styles/img/header.svg";

//Redux
import { scanToken, postProcessSSN } from "../lib/slices/paladin";

//Libs
import { datadogLogs } from "@datadog/browser-logs";
import toast, { Toaster } from "react-hot-toast";

//Hooks
import RoundLoader from "../Hooks/RoudLoader.js";

//Components
import InvitationFail from "../Components/InvitationFail";
import Error from "../Components/Results/Error.js";
import Success from "../Components/Results/Success.js";
import Header from "../Components/Common/header.js";

//constants
import { ALERTS_STYLES, ALERTS_POSITION_SELFIE } from "../constants/alerts.js";

const ComplianceCheck = () => {
    const [loading, setLoading] = useState(false);
    const { dispatch } = store;
    const urlSearchParams = new URLSearchParams(window.location.search);
    const token = urlSearchParams.get("token");
    const [tenantColor, setTenantColor] = useState(localStorage.getItem(`tenantColor_${token}`) || "#6A2ABD");
    const [tenantImage, setTenantImage] = useState(Logo);
    const [tenantName, setTenantName] = useState("");
    const [responseScan, setResponseScan] = useState(null);
    const [ssn, setSsn] = useState("");
    const [result, setResult] = useState(null); // null = form, 0 = success, 2 = error
    const [redirect, setRedirect] = useState(false);

    const queryData = async () => {
        setLoading(true);
        var data = { token };
        try {
            const response = await dispatch(scanToken({ data }));
            setResponseScan(response?.payload?.ScanToken);
            const resolvedColor = response?.payload?.ScanToken?.custom_color || "#6A2ABD";
            setTenantColor(resolvedColor);
            localStorage.setItem(`tenantColor_${token}`, resolvedColor);
            setTenantImage(
                response?.payload?.ScanToken?.custom_image &&
                `data:image/jpeg;base64,${response?.payload?.ScanToken?.custom_image}`,
            );
            setTenantName(
                response?.payload?.ScanToken?.account_name
                    ? response?.payload?.ScanToken?.account_name
                    : "",
            );
            datadogLogs.logger.log("Token validated (KYC check)", {
                category: "token-validation",
                status: "info",
                tenantName: response?.payload?.ScanToken?.account_name,
                invitationId: response?.payload?.ScanToken?.invitation_id,
            });
        } catch (error) {
            datadogLogs.logger.log("Error validating token (KYC check)", {
                category: "error-handling",
                status: "error",
                token: token,
                errorMessage: error?.message,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        queryData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSsnChange = (e) => {
        const value = e.target.value.replace(/\D/g, "").slice(0, 4);
        setSsn(value);
    };

    const handleSubmit = async () => {
        if (ssn.length !== 4) {
            toast.error("Please enter the last 4 digits of your Social Security Number.");
            return;
        }
        setLoading(true);
        try {
            const response = await dispatch(postProcessSSN({ data: { token, ssn4: ssn, slug: "ComplianceCheck" } }));
            if (response?.error) {
                datadogLogs.logger.log("Error submitting SSN (KYC check)", {
                    category: "error-handling",
                    status: "error",
                    token: token,
                    errorMessage: response?.error,
                    invitationId: responseScan?.invitation_id,
                });
                setResult(2);
            } else {
                datadogLogs.logger.log("SSN submitted (KYC check)", {
                    category: "ssn-submission",
                    status: "info",
                    tenantName: responseScan?.account_name,
                    invitationId: responseScan?.invitation_id,
                });
                setRedirect(response?.payload?.ProcessSSN?.redirect || false);
                setResult(0);
            }
        } catch (error) {
            datadogLogs.logger.log("Error submitting SSN  (KYC check)", {
                category: "error-handling",
                status: "error",
                token: token,
                errorMessage: error?.message,
            });
            setResult(2);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <main className="w-full">
                {loading ? (
                    <div className="flex flex-col gap-8 h-screen row-start-2 items-center justify-center w-full container mx-auto p-3 py-9">
                        <RoundLoader loading={loading} color={tenantColor} />
                    </div>
                ) : (
                    <>
                        {responseScan?.invitation_id && (
                            <section className="flex flex-col items-center justify-start w-full">
                                {result === 0 ? (
                                    <Success
                                        token={token}
                                        responseScan={responseScan}
                                        tenantName={tenantName}
                                        tenantColor={tenantColor}
                                        tenantImage={tenantImage}
                                        redirect={redirect}
                                    />
                                ) : result === 2 ? (
                                    <Error
                                        token={token}
                                        responseScan={responseScan}
                                        tenantName={tenantName}
                                        tenantColor={tenantColor}
                                        tenantImage={tenantImage}
                                        redirect={redirect}
                                    />
                                ) : (
                                    <section className="w-full flex flex-col items-center justify-start container mx-auto p-3 py-9">
                                        <Header
                                            tenantImage={tenantImage}
                                            tenantName={tenantName}
                                            tenantColor={tenantColor}
                                        />

                                        <h2 className="text-2xl text-center my-4">
                                            Compliance Screening
                                        </h2>

                                        <p className="text-center text-sm text-[#333333] mb-8">
                                            To complete your compliance screening, please enter the last
                                            4 digits of your Social Security Number.
                                        </p>

                                        <div className="w-full flex flex-col gap-2 mb-8">
                                            <label className="text-sm font-semibold text-[#333333]">
                                                Last 4 digits of SSN
                                            </label>
                                            <input
                                                type="number"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                maxLength={4}
                                                value={ssn}
                                                onChange={handleSsnChange}
                                                placeholder="_ _ _ _"
                                                className="w-full border border-[#B1B0B3] rounded-md px-4 py-3 text-center text-2xl tracking-[0.5em] focus:outline-none focus:border-[#6A2ABD]"
                                                style={{ caretColor: tenantColor }}
                                            />
                                        </div>

                                        <button
                                            onClick={handleSubmit}
                                            disabled={ssn.length !== 4}
                                            style={{
                                                backgroundColor: ssn.length === 4 ? tenantColor : undefined,
                                            }}
                                            className={`w-full py-2 rounded-full text-white font-semibold transition-opacity ${ssn.length === 4
                                                ? "opacity-100 cursor-pointer"
                                                : "opacity-40 cursor-not-allowed bg-gray-400"
                                                }`}
                                        >
                                            Submit
                                        </button>
                                    </section>
                                )}
                            </section>
                        )}
                        {(responseScan?.error || token === null) && (
                            <InvitationFail />
                        )}
                    </>
                )}

                <Toaster
                    position={ALERTS_POSITION_SELFIE}
                    reverseOrder={false}
                    toastOptions={ALERTS_STYLES}
                />
            </main>
        </>
    );
};

export default ComplianceCheck;
